import React, { useEffect, useMemo, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useUser } from '../UserContext';
import { adminServices } from '../services/adminServices';
import { canConfirmExternalPayment } from '../utils/paymentSettlement';
import { resolveOfferCodeType, offerCodeTypeLabel } from '../constants/offerCodeTypes';
import ExternalPaymentConfirmModal from '../components/ExternalPaymentConfirmModal';

function formatCategory(category) {
  if (category === 'accreditor_fee') return 'Accreditor fee';
  if (category === 'admin_fee') return 'Admin fee';
  return String(category || '—').replace(/_/g, ' ');
}

function formatPaidAt(ts) {
  if (!ts) return '—';
  const date = typeof ts.toDate === 'function' ? ts.toDate() : null;
  if (!date) return '—';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatLevel(level) {
  const raw = String(level || '').toLowerCase();
  if (raw === 'accredited' || raw === 'global') return 'Global';
  if (raw === 'certified') return 'Certified';
  if (raw === 'foundation') return 'Foundation';
  return level || '—';
}

function formatChannel(channel) {
  if (!channel) return '—';
  return String(channel).replace(/_/g, ' ');
}

/** Quoted GBP and the amount Stripe actually collected can diverge (zero invoices). */
function stripeCollectedNote(row) {
  if (row?.amountChargedMinor == null || !Number.isFinite(Number(row.amountChargedMinor))) return '';
  const minor = Number(row.amountChargedMinor);
  const quoted = Number(row.amountGbp);
  const ccy = String(row.chargeCurrency || 'gbp').toLowerCase();
  if (ccy !== 'gbp' || !Number.isFinite(quoted)) return '';
  if (minor === Math.round(quoted * 100)) return '';
  const collected = minor / 100;
  return `Stripe collected £${collected.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function toCsv(rows) {
  const headers = [
    'paidAt',
    'schoolName',
    'country',
    'level',
    'amountGbp',
    'channel',
    'source',
    'requestType',
    'schoolId',
    'paymentId',
  ];
  const escape = (value) => {
    const text = value == null ? '' : String(value);
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
  };
  const lines = [headers.join(',')];
  for (const row of rows) {
    lines.push(headers.map((key) => escape(row[key])).join(','));
  }
  return lines.join('\n');
}

const AdminFinance = () => {
  const { user, permissions } = useUser();
  const [view, setView] = useState('revenue');
  const [payments, setPayments] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [unsettledRegistrations, setUnsettledRegistrations] = useState([]);
  const [unsettledRenewals, setUnsettledRenewals] = useState([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(true);
  const [isLoadingEarnings, setIsLoadingEarnings] = useState(true);
  const [isLoadingUnsettled, setIsLoadingUnsettled] = useState(true);
  const [error, setError] = useState(null);
  const [confirmSchool, setConfirmSchool] = useState(null);
  const [confirmingExternalPay, setConfirmingExternalPay] = useState(false);
  const canView = !!permissions?.canManagePayments;
  const canConfirm = canConfirmExternalPayment(permissions);

  useEffect(() => {
    if (!canView) return undefined;
    const q = query(
      collection(db, 'financePayments'),
      orderBy('paidAt', 'desc'),
      limit(200)
    );
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setPayments(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setIsLoadingPayments(false);
        setError(null);
      },
      (err) => {
        console.error('financePayments listener failed:', err);
        const code = err?.code || '';
        const msg =
          code === 'permission-denied'
            ? 'Finance ledger read denied. Ensure Firestore rules are deployed and your staff account has Fees & payments permission (Senior Accreditor or Super Admin).'
            : err?.message || 'Could not load finance ledger.';
        setError(msg);
        setIsLoadingPayments(false);
      }
    );
    return () => unsub();
  }, [canView]);

  useEffect(() => {
    if (!canView) return undefined;
    const q = query(
      collection(db, 'financeEarnings'),
      orderBy('accruedAt', 'desc'),
      limit(200)
    );
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setEarnings(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setIsLoadingEarnings(false);
      },
      (err) => {
        console.error('financeEarnings listener failed:', err);
        const code = err?.code || '';
        const hint =
          code === 'permission-denied'
            ? 'Staff costs read denied — same rules/permission requirement as revenue.'
            : err?.message || 'Could not load staff earnings.';
        setError((prev) => prev || hint);
        setIsLoadingEarnings(false);
      }
    );
    return () => unsub();
  }, [canView]);

  useEffect(() => {
    if (!canView) return undefined;
    const q = query(
      collection(db, 'schools'),
      where('paymentStatus', '==', 'awaiting_external_settlement'),
      limit(100)
    );
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const rows = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((a, b) =>
            String(a.officialName || '').localeCompare(String(b.officialName || ''))
          );
        setUnsettledRegistrations(rows);
        setIsLoadingUnsettled(false);
      },
      (err) => {
        console.error('unsettled schools listener failed:', err);
        setError((prev) => prev || err?.message || 'Could not load partner queue.');
        setIsLoadingUnsettled(false);
      }
    );
    const renewalQuery = query(
      collection(db, 'schools'),
      where('renewalSettlementStatus', '==', 'awaiting_external_settlement'),
      limit(100)
    );
    const unsubRenewals = onSnapshot(
      renewalQuery,
      (snapshot) => {
        setUnsettledRenewals(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      (err) => {
        console.error('unsettled renewal listener failed:', err);
        setError((prev) => prev || err?.message || 'Could not load renewal partner queue.');
      }
    );
    return () => {
      unsub();
      unsubRenewals();
    };
  }, [canView]);

  const unsettledSchools = useMemo(() => {
    const map = new Map();
    [...unsettledRegistrations, ...unsettledRenewals].forEach((row) => map.set(row.id, row));
    return [...map.values()].sort((a, b) =>
      String(a.officialName || '').localeCompare(String(b.officialName || ''))
    );
  }, [unsettledRegistrations, unsettledRenewals]);

  const paymentSummary = useMemo(() => {
    const totalGbp = payments.reduce((sum, row) => {
      const n = Number(row.amountGbp);
      return Number.isFinite(n) ? sum + n : sum;
    }, 0);
    return { totalGbp };
  }, [payments]);

  const earningsSummary = useMemo(() => {
    const totalGbp = earnings.reduce((sum, row) => {
      if (row.status === 'pending_rates') return sum;
      const n = Number(row.amountGbp);
      return Number.isFinite(n) ? sum + n : sum;
    }, 0);
    const pending = earnings.filter((row) => row.status === 'pending_rates').length;
    return { totalGbp, pending };
  }, [earnings]);

  const handleExportCsv = () => {
    const rows = payments.map((row) => ({
      paidAt: formatPaidAt(row.paidAt),
      schoolName: row.schoolName || '',
      country: row.country || '',
      level: formatLevel(row.level),
      amountGbp: row.amountGbp != null ? row.amountGbp : '',
      channel: row.channel || '',
      source: row.source || 'live_ledger',
      requestType: row.requestType || '',
      schoolId: row.schoolId || '',
      paymentId: row.paymentId || row.id,
    }));
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `ifip-finance-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const submitPartnerConfirm = async (details) => {
    if (!confirmSchool) return;
    setConfirmingExternalPay(true);
    try {
      await adminServices.confirmExternalRegistrationPayment(
        confirmSchool.id,
        user?.displayName || user?.email,
        details
      );
      setConfirmSchool(null);
    } catch (err) {
      setError(err?.message || 'Could not confirm payment.');
    } finally {
      setConfirmingExternalPay(false);
    }
  };

  if (!canView) return null;
  if (isLoadingPayments && isLoadingEarnings && isLoadingUnsettled) {
    return <div className="admin-placeholder">Loading finance ledger…</div>;
  }

  return (
    <div className="audit-trail-view admin-finance-view">
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <button
          type="button"
          onClick={() => setView('revenue')}
          style={{
            background: 'none',
            border: 'none',
            padding: '10px 4px',
            cursor: 'pointer',
            color: view === 'revenue' ? '#138a8a' : '#94a3b8',
            borderBottom: view === 'revenue' ? '2px solid #138a8a' : '2px solid transparent',
            fontWeight: 'bold',
            fontSize: '0.85rem',
          }}
        >
          Revenue
        </button>
        <button
          type="button"
          onClick={() => setView('costs')}
          style={{
            background: 'none',
            border: 'none',
            padding: '10px 4px',
            cursor: 'pointer',
            color: view === 'costs' ? '#138a8a' : '#94a3b8',
            borderBottom: view === 'costs' ? '2px solid #138a8a' : '2px solid transparent',
            fontWeight: 'bold',
            fontSize: '0.85rem',
          }}
        >
          Staff costs
        </button>
        <button
          type="button"
          onClick={() => setView('partner-queue')}
          style={{
            background: 'none',
            border: 'none',
            padding: '10px 4px',
            cursor: 'pointer',
            color: view === 'partner-queue' ? '#138a8a' : '#94a3b8',
            borderBottom: view === 'partner-queue' ? '2px solid #138a8a' : '2px solid transparent',
            fontWeight: 'bold',
            fontSize: '0.85rem',
          }}
        >
          Partner queue
          {unsettledSchools.length > 0 && (
            <span
              style={{
                marginLeft: '8px',
                fontSize: '0.7rem',
                padding: '2px 8px',
                borderRadius: '100px',
                background: 'rgba(251, 191, 36, 0.2)',
                color: '#fbbf24',
              }}
            >
              {unsettledSchools.length}
            </span>
          )}
        </button>
      </div>

      {view === 'revenue' && (
        <>
          <div className="registry-controls" style={{ flexWrap: 'wrap', gap: '10px' }}>
            <div className="stat-pill">{payments.length} payment row(s)</div>
            <div className="stat-pill">
              Total (listed): £{paymentSummary.totalGbp.toLocaleString('en-GB')}
            </div>
            <button type="button" className="btn-activate" onClick={handleExportCsv} disabled={!payments.length}>
              Export CSV
            </button>
          </div>

          <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 16px', maxWidth: '720px' }}>
            Accreditation fee revenue (payment-level). Fee matrix editing remains under Manage Fees and Offers.
          </p>
        </>
      )}

      {view === 'costs' && (
        <>
          <div className="registry-controls" style={{ flexWrap: 'wrap', gap: '10px' }}>
            <div className="stat-pill">{earnings.length} earning row(s)</div>
            <div className="stat-pill">
              Accrued total: £{earningsSummary.totalGbp.toLocaleString('en-GB')}
            </div>
            {earningsSummary.pending > 0 && (
              <div className="stat-pill" style={{ borderColor: '#fbbf24' }}>
                {earningsSummary.pending} pending rate(s)
              </div>
            )}
          </div>

          <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 16px', maxWidth: '720px' }}>
            Staff fees accrued when accreditation cycles are finalised. Pay rates are set in Staff Management.
          </p>
        </>
      )}

      {view === 'partner-queue' && (
        <>
          <div className="registry-controls" style={{ flexWrap: 'wrap', gap: '10px' }}>
            <div className="stat-pill">{unsettledSchools.length} awaiting confirmation</div>
          </div>
          <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 16px', maxWidth: '720px' }}>
            Partner proof-of-purchase payments still to confirm: new registrations, and renewals or upgrades.
            Confirming receipt records the ledger row. You can also confirm from the school registry row.
          </p>
        </>
      )}

      {error && (
        <div className="admin-banner-warning" style={{ marginBottom: '16px' }}>
          {error}
          {error.includes('index') && (
            <span> — deploy Firestore indexes if this persists after first query.</span>
          )}
        </div>
      )}

      {view === 'revenue' && (
      <div className="log-table">
        <div className="log-header" style={{ gridTemplateColumns: '100px 1.4fr 1fr 90px 90px 110px 1fr' }}>
          <div>Paid</div>
          <div>School</div>
          <div>Country</div>
          <div>Level</div>
          <div>GBP</div>
          <div>Channel</div>
          <div>Source / type</div>
        </div>

        {!payments.length && (
          <div className="admin-placeholder" style={{ marginTop: '24px' }}>
            No payment rows yet.
          </div>
        )}

        {payments.map((row) => (
          <div
            key={row.id}
            className="log-row"
            style={{ gridTemplateColumns: '100px 1.4fr 1fr 90px 90px 110px 1fr', alignItems: 'start' }}
          >
            <div>{formatPaidAt(row.paidAt)}</div>
            <div>
              <strong>{row.schoolName || '—'}</strong>
              {row.schoolId && (
                <div className="id-tag" style={{ marginTop: '4px' }}>
                  {row.schoolId}
                </div>
              )}
            </div>
            <div style={{ fontSize: '0.82rem', lineHeight: 1.35 }}>{row.country || '—'}</div>
            <div>{formatLevel(row.level)}</div>
            <div>
              {row.amountGbp != null && Number.isFinite(Number(row.amountGbp))
                ? `£${Number(row.amountGbp).toLocaleString('en-GB')}`
                : '—'}
              {stripeCollectedNote(row) && (
                <div style={{ marginTop: '4px', fontSize: '0.72rem', color: '#fbbf24', fontWeight: 700 }}>
                  {stripeCollectedNote(row)}
                </div>
              )}
            </div>
            <div style={{ fontSize: '0.8rem' }}>{formatChannel(row.channel)}</div>
            <div style={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
              <span className="path-tag">{row.source || 'live_ledger'}</span>
              {row.requestType && (
                <span className="path-tag" style={{ marginLeft: '6px' }}>
                  {row.requestType.replace(/_/g, ' ')}
                </span>
              )}
              {row.backfill?.note && (
                <p style={{ margin: '6px 0 0', opacity: 0.85 }}>{row.backfill.note}</p>
              )}
            </div>
          </div>
        ))}
      </div>
      )}

      {view === 'costs' && (
      <div className="log-table">
        <div className="log-header" style={{ gridTemplateColumns: '100px 1.4fr 1fr 1fr 90px 90px 110px' }}>
          <div>Accrued</div>
          <div>School</div>
          <div>Payee</div>
          <div>Category</div>
          <div>Level</div>
          <div>GBP</div>
          <div>Status</div>
        </div>

        {!earnings.length && (
          <div className="admin-placeholder" style={{ marginTop: '24px' }}>
            No staff cost rows yet.
          </div>
        )}

        {earnings.map((row) => (
          <div
            key={row.id}
            className="log-row"
            style={{ gridTemplateColumns: '100px 1.4fr 1fr 1fr 90px 90px 110px', alignItems: 'start' }}
          >
            <div>{formatPaidAt(row.accruedAt)}</div>
            <div>
              <strong>{row.schoolName || '—'}</strong>
              {row.schoolId && (
                <div className="id-tag" style={{ marginTop: '4px' }}>
                  {row.schoolId}
                </div>
              )}
            </div>
            <div style={{ fontSize: '0.82rem' }}>{row.payeeName || row.payeeUid || '—'}</div>
            <div style={{ fontSize: '0.82rem' }}>{formatCategory(row.category)}</div>
            <div>{formatLevel(row.level)}</div>
            <div>
              {row.status === 'pending_rates'
                ? '—'
                : row.amountGbp != null && Number.isFinite(Number(row.amountGbp))
                  ? `£${Number(row.amountGbp).toLocaleString('en-GB')}`
                  : '—'}
            </div>
            <div style={{ fontSize: '0.78rem' }}>
              <span className="path-tag">{row.status || 'accrued'}</span>
            </div>
          </div>
        ))}
      </div>
      )}

      {view === 'partner-queue' && (
      <div className="log-table">
        <div className="log-header" style={{ gridTemplateColumns: '1.4fr 1fr 90px 90px 1fr 140px' }}>
          <div>School</div>
          <div>Country</div>
          <div>Level</div>
          <div>GBP</div>
          <div>Offer / partner</div>
          <div>Action</div>
        </div>

        {!unsettledSchools.length && (
          <div className="admin-placeholder" style={{ marginTop: '24px' }}>
            No partner payments awaiting confirmation.
          </div>
        )}

        {unsettledSchools.map((row) => {
          const isRenewal = row.renewalSettlementStatus === 'awaiting_external_settlement';
          const fee = isRenewal ? row.renewalRequest?.feeGbp : row.appliedOffer?.feeGbp;
          const codeType = isRenewal ? 'proof_of_purchase' : resolveOfferCodeType(row.appliedOffer);
          const offerCode = isRenewal ? row.renewalRequest?.offerCode : row.appliedOffer?.code;
          return (
            <div
              key={row.id}
              className="log-row"
              style={{ gridTemplateColumns: '1.4fr 1fr 90px 90px 1fr 140px', alignItems: 'start' }}
            >
              <div>
                <strong>{row.officialName || row.answers?.officialName || '—'}</strong>
                <div className="id-tag" style={{ marginTop: '4px' }}>{row.id}</div>
              </div>
              <div style={{ fontSize: '0.82rem' }}>
                {row.country || row.answers?.officialAddressCountry || '—'}
              </div>
              <div>{formatLevel(row.accreditationLevel)}</div>
              <div>
                {fee != null && Number.isFinite(Number(fee))
                  ? `£${Number(fee).toLocaleString('en-GB')}`
                  : '—'}
              </div>
              <div style={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
                <code>{offerCode || '—'}</code>
                <span className="path-tag" style={{ marginLeft: '6px' }}>
                  {isRenewal ? 'Renewal' : offerCodeTypeLabel(codeType)}
                </span>
                {row.appliedOffer?.label && (
                  <p style={{ margin: '6px 0 0', opacity: 0.85 }}>{row.appliedOffer.label}</p>
                )}
              </div>
              <div>
                {canConfirm ? (
                  <button
                    type="button"
                    className="btn-lifecycle-green"
                    style={{ width: '100%', fontSize: '0.75rem' }}
                    onClick={() => setConfirmSchool(row)}
                  >
                    Confirm…
                  </button>
                ) : (
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Senior finance only</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}

      <ExternalPaymentConfirmModal
        school={confirmSchool}
        isOpen={!!confirmSchool}
        isSubmitting={confirmingExternalPay}
        onClose={() => !confirmingExternalPay && setConfirmSchool(null)}
        onConfirm={submitPartnerConfirm}
      />
    </div>
  );
};

export default AdminFinance;
